/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable dot-notation */
import { cloneDeep, find } from 'lodash';

class Cache {
  data: { [key: string]: any };

  // eslint-disable-next-line space-before-function-paren
  constructor () {
    this.data = {};
  }

  clear (key = 'ALL') {
    if (key !== 'ALL') {
      delete this.data[key];
    } else {
      this.data = {};
    }
  }

  set (key: string, value: any, ttl: number) {
    if (ttl) {
      this.data[key] = { value, expire: Date.now() + (ttl * 1000) };
    }
  }

  has (key: string) {
    const cache = this.data[key];

    return cache && cache.expire >= Date.now();
  }

  get (key: string, ifUndefined?: any) {
    const cache = this.data[key];

    if (cache && cache.expire >= Date.now()) {
      return cache.value;
    } else {
      this.clear(key);
      if (ifUndefined && typeof ifUndefined === 'function') {
        return ifUndefined();
      } else {
        return undefined;
      }
    }
  }
}

function cachedPromise (cache: Cache, key: string, params: { [key: string]: any } = {}, ttl: number, getPromise: () => Promise<unknown>) {
  let cacheKey: string = key;
  if (typeof params !== 'object') {
    params = { v: params };
  }
  for (const k in params) {
    cacheKey += `_${k}=${params[k]}`;
  }

  if (cache.has(cacheKey)) {
    const cacheValue = cache.get(cacheKey);
    if (cacheValue.promise) {
      // console.debug('return cached promise for ' + cacheKey);
      return cacheValue.promise;
    }
    if (cacheValue.response) {
      // console.debug('return cached response for ' + cacheKey);
      return Promise.resolve(cacheValue.response);
    }
  }

  const promise = getPromise().then(response => {
    // console.debug('cache response for ' + cacheKey);
    cache.set(cacheKey, { response }, ttl);

    return Promise.resolve(response);
  }).catch(err => {
    // console.debug('clear cache for ' + cacheKey);
    cache.clear(cacheKey);
    throw err;
  });

  // console.debug('cache promise for ' + cacheKey);
  cache.set(cacheKey, { promise }, Math.min(60, ttl));

  return promise;
}

type StorageType = 'SESSION' | 'LOCAL' | 'NONE';
type SaveStrategy = StorageType | { storageType: Omit<StorageType, 'NONE'>, serialize: (value: any) => string, deserialize: (value: string) => any }

function storageSave (saveStrategy: SaveStrategy, key: string, value: any) {
  const storageType = typeof saveStrategy === 'string' ? saveStrategy : saveStrategy.storageType;
  const serialize = typeof saveStrategy === 'string' ? JSON.stringify : saveStrategy.serialize;

  if (storageType === 'SESSION') {
    window.sessionStorage.setItem(key, serialize(value));
  } else if (storageType === 'LOCAL') {
    window.localStorage.setItem(key, serialize(value));
  }
}

function storageGet (saveStrategy: SaveStrategy, key: string, defaultValue: any) {
  const storageType = typeof saveStrategy === 'string' ? saveStrategy : saveStrategy.storageType;
  const parse = typeof saveStrategy === 'string' ? JSON.parse : saveStrategy.deserialize;

  let value = null;
  if (storageType === 'SESSION') {
    value = parse(window.sessionStorage.getItem(key) as string);
  } else if (storageType === 'LOCAL') {
    value = parse(window.localStorage.getItem(key) as string);
  }
  return value !== null ? value : defaultValue;
}

function storageRemove (saveStrategy: SaveStrategy, key: string) {
  const storageType = typeof saveStrategy === 'string' ? saveStrategy : saveStrategy.storageType;
  if (storageType === 'SESSION') {
    window.sessionStorage.removeItem(key);
  } else if (storageType === 'LOCAL') {
    window.localStorage.removeItem(key);
  }
}

type State = { [key: string]: any, __cache: Cache }

export type Module = {
  namespaced: boolean;
  state: State;
  mutations: { [key: string]: Function };
  getters: { [key: string]: Function };
  actions: { [key: string]: Function };
};

type Statepart = {
  key: string,
  initialValue: any,
  saveStrategy: SaveStrategy,
  loadFunction: Function | null,
  cacheTime: number
}

type ActionsParams = {
  commit: Function,
  state: State,
  getters: Function[]
}

type Loader = { key: string, loadFunction: Function, cacheTime: number }

/**
 * @exemple
 *  usage :
 *
 *     const builder = new StoreBuilder('domain');
 *     builder.addStatePart('dataList', [], (arg1) => api.getData(arg1), 'SESSION');
 *     export default builder.build();
 *     // generate store with storage in sessionStorage :
 *     {
 *       name: 'domain'
 *       state: {
 *         dataList: []
 *       },
 *       mutations: {
 *         SET_DATALIST: f(),
 *         CLEAR_DATALIST: f(),
 *         RESET_DATALIST: f(),
 *         STORE_DATALIST: f() // store part (in sessionStorage here)
 *       },
 *       getters: {
 *         dataList: f()
 *       },
 *       actions: {
 *         setDataList: f(value)
 *         loadDataList: f(arg1) // call (arg1) => api.getData(arg1), store value in state and return it
 *         fetchDataList : alias loadDataList
 *         resetDataList : f() // reset to initial value : []
 *         clearDataList : f() // set to null
 *         reset: f()
 *       }
 *     }
 */
export default class StoreBuilder {
  name: string;
  stateParts: Statepart[];
  mutations: { [key: string]: Function };
  getters: { [key: string]: Function };
  actions: { [key: string]: Function };
  loaders: { [key: string]: Loader };

  /**
   * @see {StoreBuilder}
   */
  // eslint-disable-next-line space-before-function-paren
  constructor (name: string) {
    this.name = name;
    this.stateParts = [];
    this.mutations = {};
    this.getters = {};
    this.actions = {};
    this.loaders = {};
  }

  /**
   *
   * @param {String} key add a state property with 'key' name
   * @param {Any} initialValue inittial value of state part (if reset is called, state part return to initial value)
   * @param {Function} loadFunction generate an action named 'loadKey', store resuiilt in state
   * @param {String} saveStrategy 'NONE'|'SESSION'|'LOCAL' : default 'NONE', define storage strategy : no storage, sessionStorage, localStorage
   * @Param {Integer} cacheTime default:0, loadfuncxtion cache validity in seconds
   *
   * @description
   * add a state part 'key' with :
   *  - mutations :
   *    - SET_KEY : set state.key
   *    - CLEAR_KEY : set state.key to null
   *    - STORE_KEY : store value in storage (if storage strategy defined)
   *    - RESET_KEY : set state.key to initial value
   *  - getter :
   *    - key
   *  - actions
   *    - setKey
   *    - clearKey : set to null
   *    - resetKey : reset to inital value
   *    - loadKey : if loadFunction provided
   *    - fetchKey : alias loadkey
   *
   * a generic actions 'reset' is also generated
   *    - reset : reset all states parts to initials values
   */
  addStatePart (key: Statepart['key'], initialValue: Statepart['initialValue'], loadFunction: Statepart['loadFunction'] = null, saveStrategy: Statepart['saveStrategy'] = 'NONE', cacheTime: Statepart['cacheTime'] = 0) {
    this.stateParts.push({ key, initialValue, saveStrategy, loadFunction, cacheTime });
    return this;
  }

  /**
   *
   * @param {Loader} loaders
   *
   * @description
   * add load functions with cache manegement, without any memorisation
   * if key = 'key', generate loadKey and alias fetchKey functions
   */
  setLoaders (loaders: { [key: string]: Omit<Loader, 'key'> }) {
    this.loaders = Object.assign(this.loaders, loaders);
    return this;
  }

  /**
   * add mutations to store
   * override default mutations if same names
   *
   * @param {Object} mutations
   */
  setMutations (mutations: Module['mutations']) {
    this.mutations = Object.assign(this.mutations, mutations);
    return this;
  }

  /**
   * add getters to store
   * override default getters is same names
   *
   * @param {Object} getters
   */
  setGetters (getters: Module['getters']) {
    this.getters = Object.assign(this.getters, getters);
    return this;
  }

  /**
   * add actions to store
   * override default actions if same names
   *
   * @param {Object} actions
   */
  setActions (actions: Module['actions']) {
    this.actions = Object.assign(this.actions, actions);
    return this;
  }

  build (): Module & { name: string } {
    const __cache = new Cache();
    const module: Module & { name: string } = {
      namespaced: true,
      name: this.name,
      state: { __cache },
      mutations: {},
      getters: {},
      actions: {}
    };

    module.mutations['CLEAR_CACHE'] = (state: State) => {
      state.__cache.clear();
    };

    module.actions.clearCache = ({ commit }: ActionsParams) => {
      commit('CLEAR_CACHE');
    };

    for (const i in this.stateParts) {
      const part = this.stateParts[i];
      const key = part.key;
      const keyUpperCase = key.toUpperCase();
      const keyUpperCamelCase = key.replace(/^\w/, (c) => c.toUpperCase());
      const storageKey = this.name + '.' + key;

      module.state[key] = storageGet(part.saveStrategy, storageKey, cloneDeep(part.initialValue));

      module.mutations['SET_' + keyUpperCase] = (state: State, value: any) => {
        state[key] = value;
        storageSave(part.saveStrategy, storageKey, value);
      };
      module.mutations['CLEAR_' + keyUpperCase] = (state: State) => {
        state[key] = null;
        storageRemove(part.saveStrategy, storageKey);
      };
      module.mutations['STORE_' + keyUpperCase] = (state: State) => {
        storageSave(part.saveStrategy, storageKey, state[key]);
      };
      module.mutations['RESET_' + keyUpperCase] = (state: State) => {
        state[key] = cloneDeep(part.initialValue);
        storageSave(part.saveStrategy, storageKey, state[key]);
      };

      module.getters[key] = (state: State) => state[key];

      const clearKey = 'clear' + keyUpperCamelCase;
      module.actions[clearKey] = ({ commit }: ActionsParams, value: any) => { commit('CLEAR_' + keyUpperCase, value); };

      const resetKey = 'reset' + keyUpperCamelCase;
      module.actions[resetKey] = ({ commit }: ActionsParams, value: any) => { commit('RESET_' + keyUpperCase, value); };

      const setterKey = 'set' + keyUpperCamelCase;
      module.actions[setterKey] = ({ commit }: ActionsParams, value: any) => { commit('SET_' + keyUpperCase, value); };

      if (part.loadFunction) {
        const loaderKey = 'load' + keyUpperCamelCase;
        const loaderKey2 = 'fetch' + keyUpperCamelCase;
        module.actions[loaderKey] = ({ commit }: ActionsParams, params: any) => {
          return cachedPromise(__cache, key, params, part.cacheTime, () => (part.loadFunction as Function)(params))
            .then((value: any) => {
              commit('SET_' + keyUpperCase, value);
              return value;
            });
        };
        module.actions[loaderKey2] = module.actions[loaderKey];
      }
    }

    module.actions.reset = ({ commit }: ActionsParams, param: 'LOCAL' | 'SESSION' | 'ALL' = 'ALL') => {
      for (const mutation in module.mutations) {
        if (mutation.startsWith('RESET_')) {
          if (param !== 'ALL') {
            const key = mutation.substr(6);
            if (find(this.stateParts, sp => sp.key.toUpperCase() === key && (sp.saveStrategy === param || sp.saveStrategy === 'NONE'))) {
              commit(mutation);
            }
          } else {
            commit(mutation);
          }
        }
      }
      commit('CLEAR_CACHE');
    };

    module.mutations = Object.assign(module.mutations, this.mutations);
    module.getters = Object.assign(module.getters, this.getters);
    module.actions = Object.assign(module.actions, this.actions);

    for (const key in this.loaders) {
      const keyUpperCamelCase = key.replace(/^\w/, (c) => c.toUpperCase());

      const loader = this.loaders[key];
      const loaderKey = 'load' + keyUpperCamelCase;
      const loaderKey2 = 'fetch' + keyUpperCamelCase;
      module.actions[loaderKey] = (never: ActionsParams, params: any) => {
        return cachedPromise(__cache, key, params, loader.cacheTime, () => (loader.loadFunction as Function)(params));
      };
      module.actions[loaderKey2] = module.actions[loaderKey];
    }

    return module;
  }
}
